﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateShopRequest,
  Shop,
  UpdateShopRequest,
} from "@ticketnjoy/types";

import { meQueryKey } from "@/features/organizers/api";
import { apiClient } from "@/lib/api-client";

export const shopsQueryKey = ["shops"] as const;
export const shopQueryKey = (slug: string) => ["shops", slug] as const;

export function useShopsQuery() {
  return useQuery<Shop[]>({
    queryKey: shopsQueryKey,
    queryFn: () => apiClient<Shop[]>("/shops"),
  });
}

export function useShopQuery(slug: string) {
  return useQuery<Shop>({
    queryKey: shopQueryKey(slug),
    queryFn: () => apiClient<Shop>(`/shops/${slug}`),
    enabled: Boolean(slug),
  });
}

/**
 * Shop list/detail ve /me cache'ini taze tutmak için tüm mutation'larda ortak invalidate.
 * /me içinde de shops array'i var, güncel kalsın.
 */
function invalidateShopCaches(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: shopsQueryKey });
  qc.invalidateQueries({ queryKey: meQueryKey });
}

export function useCreateShopMutation() {
  const qc = useQueryClient();
  return useMutation<Shop, Error, CreateShopRequest>({
    mutationFn: (body) =>
      apiClient<Shop>("/shops", { method: "POST", body }),
    onSuccess: (shop) => {
      qc.setQueryData(shopQueryKey(shop.slug), shop);
      invalidateShopCaches(qc);
    },
  });
}

export function useUpdateShopMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<Shop, Error, UpdateShopRequest>({
    mutationFn: (body) =>
      apiClient<Shop>(`/shops/${slug}`, { method: "PUT", body }),
    onSuccess: (shop) => {
      qc.setQueryData(shopQueryKey(shop.slug), shop);
      invalidateShopCaches(qc);
    },
  });
}

type StatusAction = "pause" | "activate" | "archive";

export function useShopStatusMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<Shop, Error, StatusAction>({
    mutationFn: (action) =>
      apiClient<Shop>(`/shops/${slug}/${action}`, { method: "POST" }),
    onSuccess: (shop) => {
      qc.setQueryData(shopQueryKey(shop.slug), shop);
      invalidateShopCaches(qc);
    },
  });
}
